import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faLocationDot,
  faUserGroup,
  faUsersGear,
  faCheck,
  faXmark,
  faSitemap,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";

import useFetch from "../../hooks/useFetch";
// import { psGet, psPost } from "./PSApiClient";
import { chapterBannerUrl } from "./psImageUrls";
import PSImageUploader from "./PSImageUploader";
import {
  ui,
  PrimaryButton,
  GhostButton,
  DirectorBadge,
  MemberBadge,
  PendingBadge,
  StatusPill,
  EmptyState,
  ErrorState,
  Notice,
  EntityThumb,
  BannerImage,
  getRole,
  memberCountLabel,
  memberLabel,
  memberId,
} from "./psShared";

/**
 * Works in three modes:
 *  - circleId set        -> chapters affiliated with that circle (nested in PSCircles)
 *  - deepLinkSlug set     -> pins one chapter fetched by slug (shared link)
 *  - neither              -> global chapter directory (standalone "Chapters" Home tab)
 */
export default function PSChapter({ circleId = null, deepLinkSlug = null }) {
  const authState = useSelector((state) => state.authReducer);
const token = authState?.token;
const user = authState?.user;
const [fetchData] = useFetch();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", location: "", pinCode: "" });
  const [creating, setCreating] = useState(false);
  const [manageOpenFor, setManageOpenFor] = useState(null);
  const [addCode, setAddCode] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [bannerVersions, setBannerVersions] = useState({});
  const [pinned, setPinned] = useState(null);
  const [pinnedError, setPinnedError] = useState("");

  const load = async () => {
    try {
        setLoading(true);
        setError(null);

        const requestConfig = {
            // url: circleId ? "/circles/chapters" : "/chapters",
            url: circleId ? `/circles/${circleId}/chapters` : "/chapters/create",
            method: "post",
            data: {
                circleId
            },
            headers: { Authorization: token }
        };

        const response = await fetchData(requestConfig, { silent: true });

        setChapters(response?.data || response || []);
    } catch (e) {
        setError(e?.message);
    } finally {
        setLoading(false);
    }
};

  useEffect(() => {
    if (!token) return;
    load();
}, [circleId, token]);

 useEffect(() => {
    if (!deepLinkSlug) return;

    const loadPinned = async () => {
        try {
            setPinnedError("");

            const requestConfig = {
                url: `/chapters/slug/${deepLinkSlug}`,
                method: "get"
            };

            const response = await fetchData(requestConfig, { silent: true });

            setPinned(response?.data || response);
        } catch (e) {
            setPinnedError("That chapter link looks invalid or the chapter was removed.");
        }
    };

    loadPinned();
}, [deepLinkSlug]);

  const handleJoin = async (id) => {
    try {
        setError(null);
        setNotice("");

        const requestConfig = {
            url: `/chapters/${id}/join-request`,
            method: "post",
            headers: { Authorization: token }
        };

        await fetchData(requestConfig, { silent: true });

        setNotice("Join request sent — the chapter director will approve you.");
        load();
    } catch (e) {
        setError(e?.message);
    }
};

  const handleDecision = async (chapterId, uid, action) => {
    try {
        const requestConfig = {
            url: `/chapters/${chapterId}/members/${uid}/${action}`,
            method: "post",
            headers: { Authorization: token }
        };

        await fetchData(requestConfig, { silent: true });

        load();
    } catch (e) {
        setError(e?.message);
    }
};

  const handleAddMember = async (chapterId) => {
    if (!addCode.trim()) return;

    try {
        setError(null);
        setAddingMember(true);

        const requestConfig = {
            url: `/chapters/${chapterId}/members/add`,
            method: "post",
            data: {
                think5whyscode: addCode.trim()
            },
            headers: { Authorization: token }
        };

        await fetchData(requestConfig, { silent: true });

        setAddCode("");
        load();
    } catch (e) {
        setError(e?.message);
    } finally {
        setAddingMember(false);
    }
};

 const handleCreate = async (e) => {
    e.preventDefault();

    try {
        setFormError(null);
        setCreating(true);

        const requestConfig = {
            // url: circleId ? "/circles/chapters" : "/chapters",
            url: circleId ? `/circles/${circleId}/chapters` : "/chapters/create",
            method: "post",
            data: {
                circleId,
                ...form
            },
            headers: { Authorization: token }
        };

        await fetchData(requestConfig, { silent: true });

        setForm({ name: "", description: "", location: "", pinCode: "" });
        setShowForm(false);

        setNotice(
            circleId
                ? "Chapter created — pending the circle director's approval to affiliate."
                : "Chapter created — pending our team's activation before it goes live."
        );

        load();
    } catch (e) {
        setFormError(e?.message);
    } finally {
        setCreating(false);
    }
};

  const renderCard = (c, { pinnedCard = false } = {}) => {
    const { isDirector, isMember, isPending } = getRole(c, user?._id);
    const pendingRequests = c.pendingRequestIds || [];
    const activeMembers = (c.memberIds || []).filter((m) => memberId(m) !== String(c.directorUserId?._id || c.directorUserId));
    const isManaging = manageOpenFor === c._id;

    return (
      <div key={c._id} className={`${ui.card} overflow-hidden ${pinnedCard ? "border-fuchsia-400/30" : ""}`}>
        <div className="relative">
          <BannerImage src={chapterBannerUrl(c, bannerVersions[c._id])} name={c.name} className="h-20 w-full lg:h-24" />
          {isDirector && (
            <div className="absolute right-2 top-2 h-8 w-14 overflow-hidden rounded-lg opacity-90">
              <PSImageUploader
                uploadUrl="/upload-pehchaansetuchannelimage"
                codeHeader="x-channel-code"
                code={c.slug}
                currentUrl={chapterBannerUrl(c, bannerVersions[c._id])}
                shape="banner"
                onUploaded={() => setBannerVersions((v) => ({ ...v, [c._id]: Date.now() }))}
              />
            </div>
          )}
        </div>

        <div className="p-3.5">
          <div className="flex items-start gap-3">
            <div className="-mt-9">
              <EntityThumb src={chapterBannerUrl(c, bannerVersions[c._id])} name={c.name} shape="circle" size="md" />
            </div>
            <div className="min-w-0 flex-1 pt-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="truncate text-[13px] lg:text-base font-bold text-white">{c.name}</p>
                {isDirector && <DirectorBadge />}
                {!isDirector && isMember && <MemberBadge />}
                {!isDirector && !isMember && isPending && <PendingBadge />}
                {c.status && c.status !== "active" && (
                  <StatusPill tone="pending">Awaiting activation</StatusPill>
                )}
              </div>
              {c.location && (
                <p className="mt-0.5 flex items-center gap-1 text-[11px] lg:text-xs text-white/45">
                  <FontAwesomeIcon icon={faLocationDot} className="text-[10px]" />
                  {c.location}
                </p>
              )}
            </div>
          </div>

          {c.description && (
            <p className="mt-2 line-clamp-2 text-[12px] lg:text-sm text-white/60">{c.description}</p>
          )}

          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-1 text-[11px] lg:text-xs text-white/40">
              <FontAwesomeIcon icon={faUserGroup} className="text-[10px]" />
              {memberCountLabel(c)}
            </span>

            <div className="flex items-center gap-2">
              {isDirector && (
                <GhostButton onClick={() => setManageOpenFor(isManaging ? null : c._id)}>
                  <FontAwesomeIcon icon={faUsersGear} className="mr-1 text-[10px]" />
                  Manage{pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ""}
                </GhostButton>
              )}
              {!isDirector && !isMember && !isPending && (
                <PrimaryButton onClick={() => handleJoin(c._id)}>Join</PrimaryButton>
              )}
            </div>
          </div>

          {isDirector && isManaging && (
            <div className="mt-3 space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
              {pendingRequests.length > 0 && (
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300/80">
                    Pending requests
                  </p>
                  <div className="space-y-1.5">
                    {pendingRequests.map((m) => (
                      <div key={String(memberId(m))} className="flex items-center justify-between gap-2">
                        <span className="truncate text-[12px] text-white/75">{memberLabel(m)}</span>
                        <div className="flex shrink-0 gap-1.5">
                          <button onClick={() => handleDecision(c._id, memberId(m), "approve")}
                            className="grid h-7 w-7 place-items-center rounded-full bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25" aria-label="Approve">
                            <FontAwesomeIcon icon={faCheck} className="text-[11px]" />
                          </button>
                          <button onClick={() => handleDecision(c._id, memberId(m), "reject")}
                            className="grid h-7 w-7 place-items-center rounded-full bg-rose-400/15 text-rose-300 hover:bg-rose-400/25" aria-label="Decline">
                            <FontAwesomeIcon icon={faXmark} className="text-[11px]" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/40">
                  Members ({activeMembers.length})
                </p>
                {activeMembers.length === 0 ? (
                  <p className="text-[11px] text-white/35">No members besides you yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {activeMembers.map((m) => (
                      <div key={String(memberId(m))} className="flex items-center justify-between gap-2">
                        <span className="truncate text-[12px] text-white/75">{memberLabel(m)}</span>
                        <button onClick={() => handleDecision(c._id, memberId(m), "deactivate")}
                          className="rounded-full border border-rose-400/25 px-2 py-0.5 text-[10px] font-semibold text-rose-300 hover:bg-rose-400/10">
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/40">
                  Add a member directly
                </p>
                <div className="flex gap-1.5">
                  <input
                    value={addCode}
                    onChange={(e) => setAddCode(e.target.value)}
                    placeholder="Their PehchaanSetu code"
                    className={`${ui.input} flex-1`}
                  />
                  <GhostButton onClick={() => handleAddMember(c._id)} disabled={addingMember}>
                    <FontAwesomeIcon icon={faUserPlus} className="mr-1 text-[10px]" />
                    Add
                  </GhostButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={circleId ? "mt-3" : "p-4 max-w-3xl mx-auto"}>
      <div className="flex items-center justify-between gap-2">
        <h4 className={`${ui.heading} ${circleId ? "text-[13px]" : "text-base lg:text-lg"} flex items-center gap-1.5`}>
          <FontAwesomeIcon icon={faSitemap} className="text-fuchsia-300 text-[13px]" />
          {circleId ? "Chapters in this circle" : "Chapters"}
        </h4>
        <GhostButton onClick={() => { setShowForm((s) => !s); setFormError(null); }}>
          <FontAwesomeIcon icon={faPlus} className="mr-1 text-[10px]" />
          Start a chapter {user?.name}
        </GhostButton>
      </div>

      {notice && <div className="mt-2"><Notice>{notice}</Notice></div>}

      {showForm && (
        <form onSubmit={handleCreate} className={`${ui.card} mt-3 space-y-2.5 p-3.5`}>
          <p className="text-[11px] text-white/40">
            {circleId
              ? "This chapter will be sent to the circle director for approval to affiliate."
              : "New chapters need our team's activation before they're publicly listed."}
          </p>
          {formError && <ErrorState message={formError} />}
          <input required placeholder="Chapter name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={ui.input} />
          <textarea placeholder="Purpose" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} className={ui.input} />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Location" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className={ui.input} />
            <input placeholder="PIN code" value={form.pinCode}
              onChange={(e) => setForm({ ...form, pinCode: e.target.value })}
              className={ui.input} />
          </div>
          <PrimaryButton type="submit" disabled={creating}>
            {creating ? "Creating…" : "Create chapter"}
          </PrimaryButton>
        </form>
      )}

      {pinnedError && <div className="mt-3"><ErrorState message={pinnedError} /></div>}
      {pinned && (
        <div className="mt-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-fuchsia-300/80">You were invited to</p>
          {renderCard(pinned, { pinnedCard: true })}
        </div>
      )}

      <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
        {loading &&
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className={`${ui.card} h-40 animate-pulse`} />
          ))}

        {!loading && error && (
          <div className="sm:col-span-2">
            <ErrorState message={error} onRetry={load} />
          </div>
        )}

        {!loading && !error && chapters.map((c) => renderCard(c))}

        {!loading && !error && chapters.length === 0 && (
          <div className="sm:col-span-2">
            <EmptyState
              icon={faSitemap}
              title={circleId ? "No chapters in this circle yet" : "No chapters yet"}
              hint="Be the first to start one."
            />
          </div>
        )}
      </div>
    </div>
  );
}
